import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Building2, ArrowBigRight, Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchLaundries } from '@/pages/parametrizacion/laundries/Services/laundries.services';
import { useAuth, ROLES } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';

const WorkplacesPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [laundries, setLaundries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const loadLaundries = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchLaundries({ query: { estado: 'Activo' } });
      if (response.data) {
        let data = response.data;
        // Si el rol es CENTRO_LAVADO, solo mostrar su propia lavandería
        if (user?.role === ROLES.CENTRO_LAVADO || user?.role === ROLES.SUPERVISOR) {
          const myId = user?.lavanderia?._id || user?.lavanderia?.id;
          if (myId) {
            data = data.filter((l) => String(l._id) === String(myId));
          } else {
            data = [];
          }
        }
        setLaundries(data);
      } else {
        setLaundries([]);
      }

    } catch (error) {
      toast({
        title: 'Error al cargar lavanderías',
        description: error.message,
        variant: 'destructive',
      });
      setLaundries([]);
    } finally {
      setIsLoading(false);
    }
  }, [toast, user]);

  useEffect(() => {
    loadLaundries();
  }, [loadLaundries, user]);

  // Filtrar lavanderías según el término de búsqueda
  const filteredLaundries = laundries.filter((laundry) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      laundry.nombre?.toLowerCase().includes(search) ||
      laundry.direccion?.toLowerCase().includes(search) ||
      laundry.telefono?.toLowerCase().includes(search) ||
      laundry.supervisor?.toLowerCase().includes(search)
    );
  });

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="p-4 md:p-4 lg:p-4"
    >
      <div className="bg-card border border-border rounded-lg p-4 mb-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-3xl font-bold">Centros de Lavado</h1>
          
          {/* Buscador */}
          {!isLoading && laundries.length > 0 && (
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar por nombre, dirección, teléfono..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center text-muted-foreground mt-8">Cargando centros de lavado...</div>
      ) : laundries.length === 0 ? (
        <div className="text-center text-muted-foreground mt-8">
          <p>No se encontraron centros de lavado activos.</p>
          <p>Puedes registrarlos en el módulo de parametrización de lavanderías.</p>
        </div>
      ) : filteredLaundries.length === 0 ? (
        <div className="text-center text-muted-foreground mt-8">
          <p>No se encontraron centros de lavado que coincidan con la búsqueda.</p>
          <Button variant="outline" onClick={handleClearSearch} className="mt-4">
            <X className="mr-2 h-4 w-4" /> Limpiar búsqueda
          </Button>
        </div>
      ) : (
        <>
          {searchTerm && (
            <div className="mb-4 text-sm text-muted-foreground">
              Mostrando {filteredLaundries.length} de {laundries.length} centro{laundries.length !== 1 ? 's' : ''}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredLaundries.map((laundry) => (
              <WorkplaceCard key={laundry._id} laundry={laundry} />
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
};

const WorkplaceCard = ({ laundry }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="rounded-lg border border-border bg-gradient-to-br from-primary/5 to-primary/10 hover:from-primary/10 hover:to-primary/15 text-card-foreground shadow-md hover:shadow-lg hover:border-primary/50 transition-all duration-300 flex flex-col h-full">
        <CardHeader className="border-b border-border/50 pb-3">
          <div className="flex items-center gap-2">
            <div className="bg-primary/15 p-1.5 rounded-lg">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <CardTitle className="text-lg font-bold">{laundry.nombre}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 flex-grow pt-3 pb-3">
          <div className="bg-background/60 p-2 rounded-md">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Dirección</p>
            <p className="text-sm font-medium">{laundry?.direccion || 'No especificada'}  - {laundry?.ciudad || ''} </p>
          </div>
          <div className="bg-background/60 p-2 rounded-md">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Teléfono</p>
            <p className="text-sm font-medium">{laundry?.telefono || 'No especificado'}</p>
          </div>
          <div className="bg-background/60 p-2 rounded-md">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Supervisor</p>
            <p className="text-sm font-medium">{laundry.supervisor || 'No asignado'}</p>
          </div>
        </CardContent>
        <CardFooter className="pt-2">
          <Button asChild className="w-full">
            <Link to={`/centros-lavado/${laundry._id}`} state={{ workplaceName: laundry.nombre, workplaceId: laundry._id }}>
              <ArrowBigRight className="mr-2 h-4 w-4" /> Gestionar Centro
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default WorkplacesPage;